import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CurrencyDollar, Users, Package, ArrowsClockwise, CursorClick, TrendUp, ShieldWarning } from '@phosphor-icons/react';
import { useWebSocket } from '@/hooks/useWebSocket';
import LiveFeed from '@/components/shared/LiveFeed';
import api from '@/lib/api';

function KPICard({ label, value, icon: Icon, color, delay }) {
  return (
    <Card className={`bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-200 animate-fade-in stagger-${delay}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className={`text-2xl font-bold tracking-tight mt-1 font-heading ${color}`}>{value}</p>
          </div>
          <Icon size={24} className={`${color} opacity-50`} weight="duotone" />
        </div>
      </CardContent>
    </Card>
  );
}

function severityClass(s) {
  const map = { critical: 'bg-red-500/20 text-red-400 border-red-500/30', high: 'bg-red-500/10 text-red-400 border-red-500/20', medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20', low: 'bg-zinc-700/30 text-zinc-400 border-zinc-600' };
  return map[s] || map.low;
}

function payoutStatusClass(s) {
  const map = { completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', processing: 'bg-blue-500/10 text-blue-400 border-blue-500/20', pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20', failed: 'bg-red-500/10 text-red-400 border-red-500/20' };
  return map[s] || '';
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { events, connected } = useWebSocket();

  useEffect(() => {
    api.get('/admin/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-zinc-400">Failed to load dashboard data.</p>;

  const { kpis, top_affiliates, fraud_alerts, recent_payouts } = data;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Network Command Center</h1>
        <p className="text-sm text-zinc-500 mt-1">Real-time network performance overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Revenue" value={`$${(kpis.total_revenue || 0).toLocaleString()}`} icon={CurrencyDollar} color="text-emerald-400" delay={1} />
        <KPICard label="Active Affiliates" value={kpis.total_affiliates || 0} icon={Users} color="text-blue-400" delay={2} />
        <KPICard label="Active Offers" value={kpis.active_offers || 0} icon={Package} color="text-amber-400" delay={3} />
        <KPICard label="Conversion Rate" value={`${kpis.conversion_rate || 0}%`} icon={ArrowsClockwise} color="text-emerald-400" delay={4} />
        <KPICard label="Total Clicks" value={(kpis.total_clicks || 0).toLocaleString()} icon={CursorClick} color="text-blue-400" delay={5} />
        <KPICard label="Total Conversions" value={(kpis.total_conversions || 0).toLocaleString()} icon={TrendUp} color="text-emerald-400" delay={6} />
        <KPICard label="Avg EPC" value={`$${kpis.avg_epc || 0}`} icon={CurrencyDollar} color="text-blue-400" delay={7} />
        <KPICard label="Advertisers" value={kpis.total_advertisers || 0} icon={Users} color="text-amber-400" delay={8} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 animate-fade-in stagger-5">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg text-white">Top Affiliates</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Affiliate</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Revenue</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Conv.</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(top_affiliates || []).map((aff, i) => (
                  <TableRow key={i} className="border-zinc-800/50" data-testid={`affiliate-row-${i}`}>
                    <TableCell className="text-zinc-200 font-medium">{aff.name}</TableCell>
                    <TableCell className="text-emerald-400 font-mono text-sm">${(aff.revenue || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-zinc-300">{aff.conversions || 0}</TableCell>
                    <TableCell>
                      <Badge className={`${aff.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400'} text-[10px]`}>
                        {aff.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {(!top_affiliates || top_affiliates.length === 0) && (
                  <TableRow><TableCell colSpan={4} className="text-center text-zinc-500 py-8">No affiliate data yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 animate-fade-in stagger-6">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <ShieldWarning size={20} className="text-red-400" />
              Compliance Watchtower
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(fraud_alerts || []).map((alert, i) => (
                <div key={i} className="p-3 bg-zinc-950 rounded-lg border border-zinc-800" data-testid={`fraud-alert-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={`${severityClass(alert.severity)} text-[10px]`}>{alert.severity}</Badge>
                    <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{alert.type?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{alert.details}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 animate-fade-in stagger-7">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-white">Payout Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">ID</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Affiliate</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Amount</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Method</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(recent_payouts || []).map((p, i) => (
                <TableRow key={i} className="border-zinc-800/50" data-testid={`payout-row-${i}`}>
                  <TableCell className="text-zinc-500 font-mono text-xs">{p.payout_id}</TableCell>
                  <TableCell className="text-zinc-200">{p.affiliate_name}</TableCell>
                  <TableCell className="text-emerald-400 font-mono">${(p.amount || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-zinc-300 text-sm">{p.method}</TableCell>
                  <TableCell>
                    <Badge className={`${payoutStatusClass(p.status)} text-[10px]`}>{p.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <LiveFeed events={events} connected={connected} />
    </div>
  );
}
